import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, PlusCircle, Trash2 } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/supabase';

type JobListing = Database['public']['Tables']['job_listings']['Row'];

type JobFormState = {
  title: string;
  team: string;
  location: string;
  is_remote: boolean;
  employment_type: string;
  description: string;
  responsibilities: string;
  requirements: string;
  apply_url: string;
  status: 'open' | 'closed' | 'draft';
};

const emptyForm: JobFormState = {
  title: '',
  team: '',
  location: '',
  is_remote: false,
  employment_type: '',
  description: '',
  responsibilities: '',
  requirements: '',
  apply_url: '',
  status: 'open',
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState<JobListing[]>([]);
  const [formState, setFormState] = useState<JobFormState>(emptyForm);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadJobs = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('job_listings')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[AdminJobs] Failed to load job listings:', error);
      setJobs([]);
    } else {
      setJobs(data || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const handleChange = (field: keyof JobFormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = field === 'is_remote'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormState(emptyForm);
    setEditingJobId(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!formState.title.trim() || !formState.description.trim()) {
      setErrorMessage('Title and description are required.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: formState.title.trim(),
        team: formState.team.trim() || null,
        location: formState.location.trim() || null,
        is_remote: formState.is_remote,
        employment_type: formState.employment_type.trim() || null,
        description: formState.description.trim(),
        responsibilities: formState.responsibilities.trim() || null,
        requirements: formState.requirements.trim() || null,
        apply_url: formState.apply_url.trim() || null,
        status: formState.status,
        updated_at: new Date().toISOString(),
      };

      if (editingJobId) {
        const { error } = await supabase
          .from('job_listings')
          .update(payload)
          .eq('id', editingJobId);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('job_listings').insert(payload);
        if (error) throw error;
      }

      resetForm();
      await loadJobs();
    } catch (error) {
      console.error('[AdminJobs] Failed to save job listing:', error);
      setErrorMessage('Failed to save job listing. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (job: JobListing) => {
    setEditingJobId(job.id);
    setFormState({
      title: job.title,
      team: job.team || '',
      location: job.location || '',
      is_remote: job.is_remote,
      employment_type: job.employment_type || '',
      description: job.description,
      responsibilities: job.responsibilities || '',
      requirements: job.requirements || '',
      apply_url: job.apply_url || '',
      status: job.status as JobFormState['status'],
    });
  };

  const handleDelete = async (jobId: string) => {
    const confirmed = window.confirm('Delete this job listing? This cannot be undone.');
    if (!confirmed) return;

    const { error } = await supabase.from('job_listings').delete().eq('id', jobId);
    if (error) {
      console.error('[AdminJobs] Failed to delete job listing:', error);
      setErrorMessage('Failed to delete job listing.');
      return;
    }

    if (editingJobId === jobId) resetForm();
    await loadJobs();
  };

  const statusBadgeClass = useMemo(() => ({
    open: 'bg-green-600/20 text-green-400',
    closed: 'bg-red-600/20 text-red-400',
    draft: 'bg-yellow-600/20 text-yellow-400',
  }), []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#EBFFEE] mb-2">Job Board</h1>
        <p className="text-[#A9A3A3]">Create and manage careers listings.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-[#221F1F] border border-[#504A4A] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <PlusCircle className="w-5 h-5 text-rollops-accent" />
          {editingJobId ? 'Edit job listing' : 'Add new job'}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Title *</label>
            <input
              value={formState.title}
              onChange={handleChange('title')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
              placeholder="Senior Growth Manager"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Team</label>
            <input
              value={formState.team}
              onChange={handleChange('team')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
              placeholder="Growth"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Location</label>
            <input
              value={formState.location}
              onChange={handleChange('location')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
              placeholder="Remote, US"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Employment type</label>
            <input
              value={formState.employment_type}
              onChange={handleChange('employment_type')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
              placeholder="Full-time"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Status</label>
            <select
              value={formState.status}
              onChange={handleChange('status')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
            >
              <option value="open">Open</option>
              <option value="draft">Draft</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-[#A9A3A3]">
          <input
            type="checkbox"
            checked={formState.is_remote}
            onChange={handleChange('is_remote')}
            className="rounded border-[#504A4A] bg-[#221F1F]"
          />
          Remote role
        </label>

        <div>
          <label className="block text-sm text-[#A9A3A3] mb-2">Description *</label>
          <textarea
            value={formState.description}
            onChange={handleChange('description')}
            className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE] min-h-[140px]"
            placeholder="High-level overview of the role."
            required
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Responsibilities</label>
            <textarea
              value={formState.responsibilities}
              onChange={handleChange('responsibilities')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE] min-h-[120px]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Requirements</label>
            <textarea
              value={formState.requirements}
              onChange={handleChange('requirements')}
              className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE] min-h-[120px]"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-[#A9A3A3] mb-2">Apply URL</label>
          <input
            value={formState.apply_url}
            onChange={handleChange('apply_url')}
            className="w-full rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
            placeholder="https://..."
          />
        </div>

        {errorMessage && <p className="text-sm text-[#F87171]">{errorMessage}</p>}

        <div className="flex flex-wrap gap-3">
          <Button type="submit" className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]" disabled={isSaving}>
            {isSaving ? 'Saving...' : editingJobId ? 'Update job' : 'Publish job'}
          </Button>
          {editingJobId && (
            <Button type="button" className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]" onClick={resetForm}>
              Cancel edit
            </Button>
          )}
        </div>
      </form>

      <section className="space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Briefcase className="w-5 h-5 text-rollops-accent" />
          Existing listings
        </div>

        {isLoading ? (
          <div className="text-[#A9A3A3]">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="text-[#A9A3A3]">No job listings yet.</div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="bg-[#221F1F] border border-[#504A4A] rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              >
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-[#EBFFEE]">{job.title}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded ${statusBadgeClass[job.status as JobFormState['status']]}`}>
                      {job.status}
                    </span>
                  </div>
                  <div className="text-sm text-[#A9A3A3]">
                    {job.team && <span>{job.team}</span>}
                    {job.location && <span> · {job.location}</span>}
                    {job.is_remote && <span> · Remote</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]"
                    onClick={() => handleEdit(job)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    className="bg-[#1A1818] hover:bg-[#363232] text-[#EBFFEE] border border-[#504A4A]"
                    onClick={() => handleDelete(job.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
